import { FC, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { FilterBlock, FilterBlockItemBase, IFilterItem, IFilterItemRendererProps } from '@cbridge-tools/filters-panel';
import { NavigationHeader } from '@cbridge-tools/navigation-block';
import { useSelector } from '@cbridge-utils/react-redux';
import { IServer, serverListSelector } from '@cbridge-packages/server';
import { clientLpListSelector } from '@cbridge-packages/lp-client';
import { RouteSegments } from '@cbridge-packages/routing-client-admin';

const PRICES_BASE = `/${RouteSegments.Root.Dashboard}/${RouteSegments.Dashboards.Prices}`;

/** Tree item renderer: pool nodes only expand; server leaves navigate to their prices route. */
const QuotesServerNavItem: FC<IFilterItemRendererProps> = ({ item, depth }) => {
  const navigate = useNavigate();
  const { serverId } = useParams();

  const isLeaf = !item.nested?.length;
  const isSelected = isLeaf && String(item.value) === serverId;

  return (
    <FilterBlockItemBase
      item={item}
      depth={depth}
      isSelected={isSelected}
      onClick={isLeaf ? () => navigate(`${PRICES_BASE}/servers/${item.value}`) : undefined}
    />
  );
};

/** Tree item renderer for LPs (flat list); each LP navigates to its prices route. */
const QuotesLpNavItem: FC<IFilterItemRendererProps> = ({ item, depth }) => {
  const navigate = useNavigate();
  const { lpId } = useParams();

  return (
    <FilterBlockItemBase
      item={item}
      depth={depth}
      isSelected={String(item.value) === lpId}
      onClick={() => navigate(`${PRICES_BASE}/lps/${item.value}`)}
    />
  );
};

/**
 * Left-nav for the Quotes View: a "QUOTES VIEW" panel header + two SYMBOL-GROUPS-style filter trees
 * (`FilterBlock`): SERVERS → pool → server, and LIQUIDITY PROVIDERS → LP.
 */
export const QuotesViewNavigation: FC = () => {
  const { t } = useTranslation();
  const { list: servers } = useSelector(serverListSelector);
  const { list: lps } = useSelector(clientLpListSelector);
  const [serversCollapsed, setServersCollapsed] = useState(false);
  const [lpsCollapsed, setLpsCollapsed] = useState(false);

  const serverItems: IFilterItem[] = useMemo(() => {
    const byNode = new Map<string, IServer[]>();
    for (const server of servers) {
      const existing = byNode.get(server.nodeName) ?? [];
      existing.push(server);
      byNode.set(server.nodeName, existing);
    }

    return [...byNode.entries()].map(([nodeName, nodeServers]) => ({
      label: nodeName,
      value: `pool:${nodeName}`,
      nested: nodeServers.map((server) => ({ label: server.serverName, value: server.serverId })),
    }));
  }, [servers]);

  const lpItems: IFilterItem[] = useMemo(
    () => lps.map((lp) => ({ label: lp.name, value: String(lp.lpId) })),
    [lps],
  );

  return (
    <Stack sx={{
      height: '100%',
    }}>
      <NavigationHeader component={'div'} title={t('text:dashboard.quotesView.title')} />
      <Stack
        sx={{
          flex: 1,
          minHeight: 0,
        }}>
        {/* flex: 0 1 auto → each block is content-height (no fill gap) but can shrink + scroll when
            the two together overflow; leftover space falls to the bottom, pinning LP under SERVERS. */}
        <Box sx={{ flex: '0 1 auto', minHeight: 0 }}>
          <FilterBlock
            label={t('text:dashboard.quotesView.section.servers')}
            items={serverItems}
            FilterBlockItemComponent={QuotesServerNavItem}
            hasSiblings={true}
            isCollapsed={serversCollapsed}
            toggleCollapsed={() => setServersCollapsed((value) => !value)}
          />
        </Box>
        <Box sx={{ flex: '0 1 auto', minHeight: 0 }}>
          <FilterBlock
            label={t('text:dashboard.quotesView.section.liquidityProviders')}
            items={lpItems}
            FilterBlockItemComponent={QuotesLpNavItem}
            hasSiblings={true}
            isCollapsed={lpsCollapsed}
            toggleCollapsed={() => setLpsCollapsed((value) => !value)}
          />
        </Box>
      </Stack>
    </Stack>
  );
};
